import { useTranslation } from 'react-i18next';
import { Spin, Pagination } from 'antd';
import type { LatestPriceProduct } from '../../../types/presentation';
import ProductItem from './ProductItem';

interface ProductsListProps {
  products: LatestPriceProduct[];
  loading: boolean;
  onProductClick?: (productName: string) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
}

const ProductsList = ({ products, loading, onProductClick, pagination }: ProductsListProps) => {
  const { t } = useTranslation('citizenPortal');

  if (loading) {
    return (
      <div className="products-list mt-8 rounded-xl border border-card-border-color bg-card-bg-color px-4 py-6">
        <div className="flex h-32 items-center justify-center">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="products-list mt-8 rounded-xl border border-card-border-color bg-card-bg-color px-4 py-6">
      <div className="item-count">
        <p className="mb-3 text-xs font-semibold text-black">
          {t('resultsCount', { resultsCount: `${products.length}` })}
        </p>
      </div>
      {products.length === 0 ? (
        <div className="py-8 text-center text-gray-500">{t('noMonitoredProducts')}</div>
      ) : (
        products.map((product) => (
          <ProductItem
            key={product.id}
            name={product.name}
            category={product.category}
            mainCategory={product.category}
            brand={product.brand}
            presentation={product.presentation}
            baskets={[product.basketName]}
            currentPrice={product.currentPrice}
            previousPrice={product.previousPrice}
            currentPriceReportedTime={product.currentPriceReportedTime}
            previousPriceReportedTime={product.previousPriceReportedTime}
            priceChange={product.priceChange}
            priceChangePercent={product.priceChangePercent}
            onProductClick={onProductClick}
            t={t}
          />
        ))
      )}
      {pagination && (
        <div className="mt-6 flex">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} ${t('of')} ${total} ${t('products')}`
            }
            pageSizeOptions={['10', '20', '50', '100']}
            align="start"
            locale={{
              jump_to: t('goTo'),
              page: t('page'),
              items_per_page: `/ ${t('page')}`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ProductsList;
